import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  arrayUnion,
  arrayRemove,
  Unsubscribe,
  QueryConstraint,
  startAfter,
  DocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { currentOrgId, orgWhere, belongsToCurrentOrg } from '../utils/orgScope';
import { apiRequest } from '../config/api';
import { Thread, Message } from '../types/thread';
import { MemberProfile } from '../types/auth';
import {
  ValidationError,
  NotFoundError,
  PermissionError,
  logError,
  retryWithBackoff,
  getUserFriendlyMessage,
} from '../utils/errorHandler';

class ThreadService {
  private threadsCollection = 'threads';
  private messagesCollection = 'messages';
  private membersCollection = 'members';

  /**
   * Convert Firestore timestamp to Date
   */
  private timestampToDate(timestamp: any): Date {
    if (timestamp?.toDate) {
      return timestamp.toDate();
    }
    if (timestamp instanceof Date) {
      return timestamp;
    }
    return new Date(timestamp);
  }

  /**
   * Convert Firestore document to Thread object
   */
  private docToThread(docSnapshot: DocumentSnapshot): Thread {
    const data = docSnapshot.data();
    if (!data) {
      throw new Error('Thread document has no data');
    }

    return {
      id: docSnapshot.id,
      name: data.name,
      description: data.description,
      type: data.type,
      entityId: data.entityId,
      entityName: data.entityName,
      imageUrl: data.imageUrl,
      participants: data.participants || [],
      adminIds: data.adminIds || [],
      lastMessage: data.lastMessage
        ? {
            ...data.lastMessage,
            timestamp: this.timestampToDate(data.lastMessage.timestamp),
          }
        : undefined,
      unreadCount: data.unreadCount || 0,
      mutedBy: data.mutedBy || [],
      createdBy: data.createdBy,
      createdAt: this.timestampToDate(data.createdAt),
      updatedAt: this.timestampToDate(data.updatedAt),
    };
  }

  /**
   * Convert Firestore document to Message object
   */
  private docToMessage(docSnapshot: DocumentSnapshot): Message {
    const data = docSnapshot.data();
    if (!data) {
      throw new Error('Message document has no data');
    }

    return {
      id: docSnapshot.id,
      threadId: data.threadId,
      senderId: data.senderId,
      senderName: data.senderName,
      senderImageUrl: data.senderImageUrl,
      content: data.content,
      type: data.type || 'text',
      attachmentUrl: data.attachmentUrl,
      timestamp: this.timestampToDate(data.timestamp),
      readBy: data.readBy || [],
    };
  }

  /**
   * Get all threads for a member based on their profile
   * Matches mobile app logic for entity-based filtering
   */
  async getThreads(memberProfile: MemberProfile): Promise<Thread[]> {
    try {
      if (!memberProfile || !memberProfile.id) {
        throw new ValidationError('Valid member profile is required');
      }

      // Canonical source: the backend API. It applies org scoping, hierarchy
      // visibility, hidden-thread filtering, and per-member unread state
      // (unreadCount for participant threads, hasUnread dot for audience
      // threads) — one implementation shared with mobile.
      const result = await apiRequest<{ success: boolean; threads: any[] }>('/api/threads');

      const threads: Thread[] = (result.threads || []).map((t: any) => ({
        id: t.id,
        name: t.name || '',
        description: t.description,
        type: t.type || 'custom',
        entityId: t.entityId,
        entityName: t.entityName,
        imageUrl: t.imageUrl,
        participants: t.participants || [],
        adminIds: t.adminIds,
        lastMessage: t.lastMessage
          ? {
              id: t.lastMessage.id,
              threadId: t.id,
              senderId: t.lastMessage.senderId,
              senderName: t.lastMessage.senderName || '',
              content: t.lastMessage.content || '',
              type: t.lastMessage.type || 'text',
              timestamp: t.lastMessage.timestamp ? new Date(t.lastMessage.timestamp) : new Date(),
            }
          : undefined,
        unreadCount: t.unreadCount || 0,
        hasUnread: !!t.hasUnread,
        mutedBy: t.mutedBy,
        createdBy: t.createdBy,
        createdAt: t.createdAt ? new Date(t.createdAt) : new Date(),
        updatedAt: t.updatedAt ? new Date(t.updatedAt) : new Date(),
      })) as Thread[];

      console.log(`✅ ThreadService: Loaded ${threads.length} threads from API`);
      return threads;
    } catch (error) {
      console.error('❌ ThreadService: Error fetching threads:', error);
      throw new Error('Failed to fetch threads');
    }
  }

  /**
   * Mark a thread read for the current member (per-member read state).
   * Fire on thread open; the caller should optimistically zero the local count.
   */
  async markThreadRead(threadId: string): Promise<void> {
    try {
      await apiRequest(`/api/threads/${threadId}/read`, { method: 'POST' });
    } catch (error) {
      console.warn('markThreadRead failed:', error);
    }
  }

  /**
   * Get entity IDs from thread's audience
   */
  private getThreadAudienceEntityIds(thread: Thread): string[] {
    // Check for audienceEntityIds first (new format)
    if ((thread as any).audienceEntityIds && Array.isArray((thread as any).audienceEntityIds)) {
      return (thread as any).audienceEntityIds.filter(
        (id: string) => id && id !== 'unknown' && id !== '',
      );
    }

    // Fallback to entityId (old format)
    if (
      thread.entityId &&
      thread.entityId !== 'unknown' &&
      thread.entityId !== 'group-chat' &&
      thread.entityId !== 'direct-message' &&
      thread.entityId !== ''
    ) {
      return [thread.entityId];
    }

    return [];
  }

  /**
   * Get hierarchical entity IDs including parent entities
   * Matches mobile app logic exactly
   */
  private async getHierarchicalEntityIds(userEntityIds: string[]): Promise<string[]> {
    try {
      if (userEntityIds.length === 0) {
        return [];
      }

      // Fetch all entities
      const { collection, getDocs } = await import('firebase/firestore');
      const entitiesRef = collection(db, 'entities');
      const entitiesSnapshot = await getDocs(entitiesRef);

      // Build entity map
      const entityMap = new Map<string, any>();
      entitiesSnapshot.forEach(doc => {
        if (!belongsToCurrentOrg(doc.data())) return;
        entityMap.set(doc.id, { id: doc.id, ...doc.data() });
      });

      // Collect all entity IDs including parents
      const allEntityIds = new Set<string>(userEntityIds);

      // For each user entity, traverse up the hierarchy
      for (const entityId of userEntityIds) {
        let currentEntity = entityMap.get(entityId);
        let safetyCounter = 0;
        const maxDepth = 10;

        while (currentEntity && safetyCounter < maxDepth) {
          const parentId = currentEntity.parentEntityId || currentEntity.parentId;
          if (!parentId) {
            break;
          }
          allEntityIds.add(parentId);
          const nextEntity = entityMap.get(parentId);
          if (!nextEntity) {
            break;
          }
          currentEntity = nextEntity;
          safetyCounter += 1;
        }
      }

      // Add national/grand-chapter access if user has any entity memberships
      if (allEntityIds.size > 0) {
        allEntityIds.add('national');
        allEntityIds.add('grand-chapter');
      }

      console.log(
        `🔍 ThreadService: Hierarchical entity IDs (${allEntityIds.size}):`,
        Array.from(allEntityIds),
      );
      return Array.from(allEntityIds);
    } catch (error) {
      console.error('❌ ThreadService: Error getting hierarchical entity IDs:', error);
      // Fallback to direct memberships
      return userEntityIds;
    }
  }

  /**
   * Get a single thread by ID
   */
  async getThread(threadId: string): Promise<Thread> {
    try {
      const threadRef = doc(db, this.threadsCollection, threadId);
      const threadDoc = await getDoc(threadRef);

      if (!threadDoc.exists() || !belongsToCurrentOrg(threadDoc.data())) {
        throw new Error('Thread not found');
      }

      return this.docToThread(threadDoc);
    } catch (error) {
      console.error('Error fetching thread:', error);
      throw new Error('Failed to fetch thread');
    }
  }

  /**
   * Create a new group chat
   */
  async createGroupChat(
    name: string,
    participants: string[],
    creatorId: string,
    imageUrl?: string,
    description?: string,
    adminIds: string[] = [],
  ): Promise<Thread> {
    try {
      const threadsRef = collection(db, this.threadsCollection);
      const now = Timestamp.now();

      const threadData = {
        name,
        description: description || '',
        type: 'group',
        imageUrl: imageUrl || '',
        participants,
        adminIds,
        unreadCount: 0,
        organizationId: currentOrgId(),
        createdAt: now,
        updatedAt: now,
        createdBy: creatorId,
      };

      const docRef = await addDoc(threadsRef, threadData);
      const threadDoc = await getDoc(docRef);

      return this.docToThread(threadDoc);
    } catch (error) {
      console.error('Error creating group chat:', error);
      throw new Error('Failed to create group chat');
    }
  }

  /**
   * Update group chat details
   */
  async updateGroupChat(threadId: string, updates: Partial<Thread>): Promise<void> {
    try {
      const threadRef = doc(db, this.threadsCollection, threadId);
      const updateData: any = {
        updatedAt: Timestamp.now(),
      };

      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.imageUrl !== undefined) updateData.imageUrl = updates.imageUrl;
      if (updates.participants !== undefined) updateData.participants = updates.participants;
      if (updates.adminIds !== undefined) updateData.adminIds = updates.adminIds;

      await updateDoc(threadRef, updateData);
    } catch (error) {
      console.error('Error updating group chat:', error);
      throw new Error('Failed to update group chat');
    }
  }

  /**
   * Update thread (alias for updateGroupChat for consistency)
   */
  async updateThread(threadId: string, updates: Partial<Thread>): Promise<void> {
    return this.updateGroupChat(threadId, updates);
  }

  /**
   * Delete a thread
   */
  async deleteThread(threadId: string): Promise<void> {
    try {
      const threadRef = doc(db, this.threadsCollection, threadId);

      // Note: In a production app, you might want to:
      // 1. Delete all messages in the thread
      // 2. Remove thread from participants' lists
      // 3. Archive instead of delete
      // For now, we'll just delete the thread document

      await updateDoc(threadRef, {
        deleted: true,
        deletedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error deleting thread:', error);
      throw new Error('Failed to delete thread');
    }
  }

  /**
   * Hide a thread for a specific member
   */
  /**
   * Per-member view flags. Pinning or archiving changes one person's list, not
   * the conversation, so these mirror the mute endpoints rather than editing
   * the thread itself.
   */
  async setThreadFlag(
    threadId: string,
    flag: 'pin' | 'archive' | 'mute',
    on: boolean,
  ): Promise<void> {
    await apiRequest(`/api/threads/${threadId}/${flag}`, {
      method: on ? 'POST' : 'DELETE',
      ...(on ? { body: JSON.stringify({}) } : {}),
    });
  }

  async hideThread(threadId: string, memberId: string): Promise<void> {
    try {
      const memberRef = doc(db, this.membersCollection, memberId);
      await updateDoc(memberRef, {
        hiddenThreads: arrayUnion(threadId),
      });
    } catch (error) {
      console.error('Error hiding thread:', error);
      throw new Error('Failed to hide thread');
    }
  }

  /**
   * Unhide a thread for a specific member
   */
  async unhideThread(threadId: string, memberId: string): Promise<void> {
    try {
      const memberRef = doc(db, this.membersCollection, memberId);
      await updateDoc(memberRef, {
        hiddenThreads: arrayRemove(threadId),
      });
    } catch (error) {
      console.error('Error unhiding thread:', error);
      throw new Error('Failed to unhide thread');
    }
  }

  /**
   * Create or get existing direct message thread between two members
   */
  async createOrGetDirectMessage(member1Id: string, member2Id: string): Promise<Thread> {
    try {
      // Check if direct message already exists (using mobile app format)
      const threadsRef = collection(db, this.threadsCollection);
      const q = query(
        threadsRef,
        orgWhere(),
        where('type', '==', 'custom'),
        where('participants', 'array-contains', member1Id),
      );

      const querySnapshot = await getDocs(q);
      let existingThread: Thread | null = null;

      querySnapshot.forEach(doc => {
        const thread = this.docToThread(doc);
        // Check for DM threads: custom type, 2 participants, and includes both users
        if (
          thread.participants.includes(member2Id) &&
          thread.participants.length === 2 &&
          (thread as any).entityId === 'direct-message'
        ) {
          existingThread = thread;
        }
      });

      if (existingThread) {
        return existingThread;
      }

      // Get member profiles to create thread name
      const member1Doc = await getDoc(doc(db, this.membersCollection, member1Id));
      const member2Doc = await getDoc(doc(db, this.membersCollection, member2Id));

      if (
        !member1Doc.exists() ||
        !member2Doc.exists() ||
        !belongsToCurrentOrg(member1Doc.data()) ||
        !belongsToCurrentOrg(member2Doc.data())
      ) {
        throw new Error('Member profiles not found');
      }

      const member1Data = member1Doc.data();
      const member2Data = member2Doc.data();

      // Create thread name matching mobile format
      const threadName = `${member1Data.firstName} ${member1Data.lastName} & ${member2Data.firstName} ${member2Data.lastName}`;

      // Create new direct message thread (matching mobile app format)
      const now = Timestamp.now();
      const threadData = {
        name: threadName,
        description: 'Direct message conversation',
        type: 'custom',
        entityId: 'direct-message', // Special entity ID for DMs
        participants: [member1Id, member2Id],
        permissions: {
          adminOnlyPost: false,
          allowReplies: true,
          allowReactions: true,
        },
        audienceType: 'custom',
        audienceEntityIds: [],
        unreadCount: 0,
        organizationId: currentOrgId(),
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await addDoc(threadsRef, threadData);
      const threadDoc = await getDoc(docRef);

      const newThread = this.docToThread(threadDoc);
      console.log('✅ ThreadService: Created new DM thread:', newThread.id);
      return newThread;
    } catch (error) {
      console.error('Error creating/getting direct message:', error);
      throw new Error('Failed to create or get direct message');
    }
  }

  /**
   * Get messages for a thread with pagination
   */
  async getMessages(threadId: string, limitCount: number = 50, before?: Date): Promise<Message[]> {
    try {
      const messagesRef = collection(db, this.threadsCollection, threadId, this.messagesCollection);

      const constraints: QueryConstraint[] = [orderBy('timestamp', 'desc'), limit(limitCount)];

      if (before) {
        constraints.push(startAfter(Timestamp.fromDate(before)));
      }

      const q = query(messagesRef, ...constraints);
      const querySnapshot = await getDocs(q);
      const messages: Message[] = [];

      querySnapshot.forEach(doc => {
        messages.push(this.docToMessage(doc));
      });

      // Return in chronological order (oldest first)
      return messages.reverse();
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw new Error('Failed to fetch messages');
    }
  }

  /**
   * Get recent media messages (images/videos) from a thread
   */
  async getRecentMedia(threadId: string, maxItems: number = 12): Promise<Message[]> {
    try {
      // Fetch more messages than needed since we filter client-side
      const messages = await this.getMessages(threadId, 100);
      return messages
        .filter(m => (m.type === 'image' || m.type === 'video') && m.attachmentUrl)
        .slice(-maxItems)
        .reverse(); // newest first
    } catch (error) {
      console.error('Error fetching recent media:', error);
      return [];
    }
  }

  /**
   * Send a message in a thread
   * Routes through backend API to trigger push notifications.
   * Falls back to direct Firestore write if API fails.
   */
  /** Members who can be @mentioned in this thread — powers the composer picker. */
  async getMentionable(
    threadId: string,
    q: string,
  ): Promise<Array<{ memberId: string; name: string; photoUrl?: string | null }>> {
    try {
      const res = await apiRequest<{
        success: boolean;
        members: Array<{ memberId: string; name: string; photoUrl?: string | null }>;
      }>(`/api/threads/${threadId}/mentionable?q=${encodeURIComponent(q)}`);
      return res.members || [];
    } catch {
      return [];
    }
  }

  async sendMessage(
    threadId: string,
    senderId: string,
    senderName: string,
    content: string,
    senderImageUrl?: string,
    _unused?: undefined, // Placeholder for backwards compatibility
    attachmentUrl?: string, // Mobile format
    type?: 'text' | 'image' | 'file' | 'video', // Mobile format
    mentionedMemberIds?: string[],
  ): Promise<Message> {
    try {
      // Try backend API first (triggers notifications)
      const response = await apiRequest<{
        success: boolean;
        message: {
          id: string;
          threadId: string;
          senderId: string;
          senderName: string;
          senderImageUrl: string;
          content: string;
          type: string;
          attachmentUrl?: string;
          timestamp: string;
          readBy: string[];
          reactions: any[];
        };
      }>(`/api/threads/${threadId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          content,
          type: type || 'text',
          attachmentUrl,
          mentionedMemberIds:
            mentionedMemberIds && mentionedMemberIds.length ? mentionedMemberIds : undefined,
        }),
      });

      return {
        id: response.message.id,
        threadId: response.message.threadId,
        senderId: response.message.senderId,
        senderName: response.message.senderName,
        senderImageUrl: response.message.senderImageUrl,
        content: response.message.content,
        type: (response.message.type as Message['type']) || 'text',
        attachmentUrl: response.message.attachmentUrl,
        timestamp: new Date(response.message.timestamp),
        readBy: response.message.readBy || [],
      };
    } catch (apiError) {
      console.warn('Backend message API failed, falling back to direct Firestore write:', apiError);

      // Fallback: write directly to Firestore (no notifications)
      const messagesRef = collection(db, this.threadsCollection, threadId, this.messagesCollection);
      const now = Timestamp.now();

      const messageData: any = {
        threadId,
        senderId,
        senderName,
        senderImageUrl: senderImageUrl || '',
        content,
        type: type || 'text',
        timestamp: now,
        readBy: [senderId],
        reactions: [],
        organizationId: currentOrgId(),
      };

      if (attachmentUrl) {
        messageData.attachmentUrl = attachmentUrl;
      }

      const docRef = await addDoc(messagesRef, messageData);

      const threadRef = doc(db, this.threadsCollection, threadId);
      await updateDoc(threadRef, {
        lastMessage: {
          id: docRef.id,
          senderId,
          senderName,
          content,
          type: type || 'text',
          timestamp: now,
        },
        updatedAt: now,
      });

      const messageDoc = await getDoc(docRef);
      return this.docToMessage(messageDoc);
    }
  }

  /**
   * Mark a message as read by a member
   */
  async markAsRead(threadId: string, messageId: string, memberId: string): Promise<void> {
    try {
      const messageRef = doc(
        db,
        this.threadsCollection,
        threadId,
        this.messagesCollection,
        messageId,
      );
      await updateDoc(messageRef, {
        readBy: arrayUnion(memberId),
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw new Error('Failed to mark message as read');
    }
  }

  /**
   * Subscribe to real-time updates for a specific thread's messages
   */
  subscribeToThread(threadId: string, callback: (messages: Message[]) => void): Unsubscribe {
    const messagesRef = collection(db, this.threadsCollection, threadId, this.messagesCollection);
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    return onSnapshot(
      q,
      querySnapshot => {
        const messages: Message[] = [];
        querySnapshot.forEach(doc => {
          messages.push(this.docToMessage(doc));
        });
        callback(messages);
      },
      error => {
        console.error('Error in thread subscription:', error);
      },
    );
  }

  /**
   * Subscribe to real-time updates for all threads of a member
   * Matches mobile app logic for entity-based filtering
   */
  subscribeToThreads(
    memberProfile: MemberProfile,
    callback: (threads: Thread[]) => void,
  ): Unsubscribe {
    const threadsRef = collection(db, this.threadsCollection);
    // Subscribe to ALL threads (matching mobile logic)
    const q = query(threadsRef, orgWhere(), orderBy('updatedAt', 'desc'));

    return onSnapshot(
      q,
      async querySnapshot => {
        const allThreads: Thread[] = [];
        querySnapshot.forEach(doc => {
          allThreads.push(this.docToThread(doc));
        });

        // Extract entity IDs from member's entity memberships
        let userEntityIds: string[] = [];
        if (memberProfile.entityMemberships && Array.isArray(memberProfile.entityMemberships)) {
          userEntityIds = memberProfile.entityMemberships
            .filter((membership: any) => membership.status === 'active')
            .map((membership: any) => membership.entityId);
        }

        // Get hierarchical access (matching mobile logic)
        const hierarchicalEntityIds = await this.getHierarchicalEntityIds(userEntityIds);

        // Filter threads by entity membership and participant status
        const filteredThreads = allThreads.filter((thread: Thread) => {
          // Special handling for group chats and direct messages
          // These should be visible to participants regardless of entity membership
          if (thread.entityId === 'group-chat' || thread.entityId === 'direct-message') {
            const isParticipant = thread.participants.includes(memberProfile.id);

            return isParticipant;
          }

          // Get thread's audience entity IDs
          const audienceEntityIds = this.getThreadAudienceEntityIds(thread);

          // Allow threads with no scoped entity (global/national)
          if (audienceEntityIds.length === 0) {
            return true;
          }

          // Check if user has hierarchical access to any of the thread's audience entities
          const hasAccess = audienceEntityIds.some(entityId =>
            hierarchicalEntityIds.includes(entityId),
          );

          return hasAccess;
        });

        // Filter out hidden threads
        const visibleThreads = filteredThreads.filter(
          thread =>
            !memberProfile.hiddenThreads || !memberProfile.hiddenThreads.includes(thread.id),
        );

        callback(visibleThreads);
      },
      error => {
        console.error('❌ ThreadService: Error in threads subscription:', error);
      },
    );
  }

  /**
   * Get unread message count for a thread
   */
  async getUnreadCount(threadId: string, memberId: string): Promise<number> {
    try {
      const messagesRef = collection(db, this.threadsCollection, threadId, this.messagesCollection);
      const q = query(messagesRef, orderBy('timestamp', 'desc'));

      const querySnapshot = await getDocs(q);
      let unreadCount = 0;

      querySnapshot.forEach(doc => {
        const message = this.docToMessage(doc);
        if (!message.readBy.includes(memberId)) {
          unreadCount++;
        }
      });

      return unreadCount;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Mark all messages in a thread as read
   */
  async markThreadAsRead(threadId: string, memberId: string): Promise<void> {
    try {
      const messagesRef = collection(db, this.threadsCollection, threadId, this.messagesCollection);
      const q = query(messagesRef);

      const querySnapshot = await getDocs(q);
      const updatePromises: Promise<void>[] = [];

      querySnapshot.forEach(doc => {
        const message = this.docToMessage(doc);
        if (!message.readBy.includes(memberId)) {
          const messageRef = doc.ref;
          updatePromises.push(
            updateDoc(messageRef, {
              readBy: arrayUnion(memberId),
            }),
          );
        }
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking thread as read:', error);
      throw new Error('Failed to mark thread as read');
    }
  }

  /**
   * Add a participant to a group thread
   */
  async addParticipant(threadId: string, userId: string, requestingUserId?: string): Promise<void> {
    try {
      console.log('🔍 ThreadService: Adding participant to thread:', {
        threadId,
        userId,
        requestingUserId,
      });

      const threadRef = doc(db, this.threadsCollection, threadId);
      const threadDoc = await getDoc(threadRef);

      if (!threadDoc.exists() || !belongsToCurrentOrg(threadDoc.data())) {
        throw new Error('Thread not found');
      }

      const thread = this.docToThread(threadDoc);

      // Check if user is already a participant
      if (thread.participants?.includes(userId)) {
        console.log('⚠️ User is already a participant');
        return;
      }

      // Check permissions if requestingUserId is provided
      if (requestingUserId) {
        const isCreator = thread.createdBy === requestingUserId;
        const isAdmin = thread.adminIds?.includes(requestingUserId) || false;

        if (!isCreator && !isAdmin) {
          throw new Error('Only group creators and administrators can add participants');
        }
      }

      // Add user to participants array
      await updateDoc(threadRef, {
        participants: arrayUnion(userId),
        updatedAt: Timestamp.now(),
      });

      console.log('✅ ThreadService: Participant added successfully');
    } catch (error) {
      console.error('❌ ThreadService: Error adding participant:', error);
      throw error;
    }
  }

  /**
   * Remove a participant from a group thread
   */
  async removeParticipant(
    threadId: string,
    userId: string,
    requestingUserId?: string,
  ): Promise<void> {
    try {
      console.log('🔍 ThreadService: Removing participant from thread:', {
        threadId,
        userId,
        requestingUserId,
      });

      const threadRef = doc(db, this.threadsCollection, threadId);
      const threadDoc = await getDoc(threadRef);

      if (!threadDoc.exists() || !belongsToCurrentOrg(threadDoc.data())) {
        throw new Error('Thread not found');
      }

      const thread = this.docToThread(threadDoc);

      // Check if user is a participant
      if (!thread.participants?.includes(userId)) {
        console.log('⚠️ User is not a participant');
        return;
      }

      // Don't allow removing the creator
      if (thread.createdBy === userId) {
        throw new Error('Cannot remove the group creator');
      }

      // Check permissions if requestingUserId is provided
      if (requestingUserId) {
        const isCreator = thread.createdBy === requestingUserId;
        const isAdmin = thread.adminIds?.includes(requestingUserId) || false;
        const isSelfRemoval = userId === requestingUserId;

        if (!isCreator && !isAdmin && !isSelfRemoval) {
          throw new Error(
            'Only group creators, administrators, or the user themselves can remove participants',
          );
        }
      }

      // Remove user from participants array
      await updateDoc(threadRef, {
        participants: arrayRemove(userId),
        updatedAt: Timestamp.now(),
      });

      console.log('✅ ThreadService: Participant removed successfully');
    } catch (error) {
      console.error('❌ ThreadService: Error removing participant:', error);
      throw error;
    }
  }
}

export const threadService = new ThreadService();
export default threadService;
